import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import api from '../../utils/api'
import Button from '../ui/Button'
import Alert from '../ui/Alert'
import { downloadCsv, downloadJson } from '../../utils/exportUtils'
import ar from '../../locales/ar.json'
import { Search } from 'lucide-react'

const t  = ar.admin
const ta = ar.app

const STATUS_BADGES = {
  draft:            'badge-stone',
  submitted:        'badge-blue',
  'pending review': 'badge-yellow',
  documented:       'badge-green',
  rejected:         'badge-red',
}

const STATUS_LABELS = {
  draft:            'مسودة',
  submitted:        'مقدَّم',
  'pending review': 'قيد المراجعة',
  documented:       'موثَّق',
  rejected:         'مرفوض',
}

const ROWS_PER_PAGE = 25

export default function CaseList() {
  const [cases,   setCases]   = useState([])
  const [total,   setTotal]   = useState(0)
  const [page,    setPage]    = useState(1)
  const [filters, setFilters] = useState({ status: '', region: '', q: '' })
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const [selected, setSelected] = useState(new Set())

  const fetchCases = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = {
        page,
        limit: ROWS_PER_PAGE,
        ...(filters.status  && { status: filters.status  }),
        ...(filters.region  && { region: filters.region  }),
        ...(filters.q.trim() && { q:      filters.q.trim() }),
      }
      const { data } = await api.get('admin/cases', { params })
      setCases(data.cases || [])
      setTotal(data.total || 0)
    } catch (err) {
      setError(err.message || t.errorLoading)
    } finally {
      setLoading(false)
    }
  }, [page, filters])

  useEffect(() => { fetchCases() }, [fetchCases])

  // Reset page when filters change
  useEffect(() => { setPage(1) }, [filters])

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function toggleAll() {
    setSelected((prev) =>
      prev.size === cases.length ? new Set() : new Set(cases.map((c) => c.id))
    )
  }

  async function handleExportSelected(format) {
    const ids = [...selected]
    if (ids.length === 0) return
    try {
      const { data } = await api.post('admin/cases/export', { ids, format })
      format === 'csv' ? downloadCsv(data) : downloadJson(data, `cases_${Date.now()}.json`)
    } catch { /* ignore */ }
  }

  const totalPages = Math.ceil(total / ROWS_PER_PAGE)

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h1 className="text-lg font-bold text-stone-800">{t.cases}</h1>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" disabled={selected.size === 0} onClick={() => handleExportSelected('csv')}>
            ⬇ CSV
          </Button>
          <Button variant="secondary" size="sm" disabled={selected.size === 0} onClick={() => handleExportSelected('json')}>
            ⬇ JSON
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 right-3 text-stone-400 pointer-events-none" />
          <input
            type="search"
            placeholder="بحث بالاسم أو الرقم..."
            value={filters.q}
            onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
            className="input-base w-full pr-8"
            dir="rtl"
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
          className="input-base w-40"
          dir="rtl"
        >
          <option value="">كل الحالات</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <Button variant="ghost" size="sm" onClick={fetchCases} title={ta.refresh}>↻</Button>
      </div>

      {error && <Alert type="error" message={error} className="mb-4" />}

      {/* Table */}
      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm text-right">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="px-4 py-3 w-8">
                <input type="checkbox" className="w-4 h-4"
                  checked={cases.length > 0 && selected.size === cases.length}
                  onChange={toggleAll} />
              </th>
              <th className="px-4 py-3 font-semibold text-stone-600">الرقم</th>
              <th className="px-4 py-3 font-semibold text-stone-600">اسم الشهيد</th>
              <th className="px-4 py-3 font-semibold text-stone-600">المنطقة</th>
              <th className="px-4 py-3 font-semibold text-stone-600">الحالة</th>
              <th className="px-4 py-3 font-semibold text-stone-600">تاريخ التقديم</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-10 text-stone-400">{ta.loading}</td></tr>
            ) : cases.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-stone-400">{t.noCases}</td></tr>
            ) : cases.map((c) => (
              <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="px-4 py-3">
                  <input type="checkbox" className="w-4 h-4"
                    checked={selected.has(c.id)}
                    onChange={() => toggleSelect(c.id)} />
                </td>
                <td className="px-4 py-3 font-mono text-xs text-stone-500">{c.readable_id}</td>
                <td className="px-4 py-3 font-medium text-stone-800">{c.martyr_fullname || '—'}</td>
                <td className="px-4 py-3 text-stone-600">{c.region || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`badge ${STATUS_BADGES[c.status] || 'badge-stone'}`}>
                    {STATUS_LABELS[c.status] || c.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-stone-400 text-xs">
                  {c.submitted_at ? new Date(c.submitted_at).toLocaleDateString('ar-SY') : '—'}
                </td>
                <td className="px-4 py-3">
                  <Link to={`/admin/cases/${c.id}`} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
                    عرض
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-4 text-sm">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ‹ السابق
          </Button>
          <span className="text-stone-500">
            {page} / {totalPages} ({total} حالة)
          </span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            التالي ›
          </Button>
        </div>
      )}
    </div>
  )
}
